import { useEffect, useState } from "react";
import {
  onAuthStateChanged,
  User,
  signInAnonymously,
  setPersistence,
  browserLocalPersistence,
  signOut as fbSignOut,
} from "firebase/auth";
import { doc, setDoc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { loadStoredUsername } from "../lib/username";

export interface UserProfile {
  uid: string;
  screenName: string;
  avatarUrl?: string | null;
  tosAcceptedAt: string;
  privacyAcceptedAt: string;
  /** Lifetime / paid unlock mirrored from the native purchase system. */
  isUnlocked?: boolean;
}

// ---- Module-level shared auth store -------------------------------------
// We keep a single source of truth for the Firebase auth + Firestore profile
// so that any component calling `useAuth` gets the already-resolved values
// immediately (instead of briefly seeing `user=null` on mount, which was
// causing route guards in child pages to redirect back to "/").
interface AuthSnapshot {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
}

let snapshot: AuthSnapshot = { user: null, profile: null, loading: true };
const listeners = new Set<(s: AuthSnapshot) => void>();

function setSnapshot(next: Partial<AuthSnapshot>) {
  snapshot = { ...snapshot, ...next };
  for (const l of listeners) l(snapshot);
}

let anonymousSignInInFlight: Promise<void> | null = null;
let profileUnsub: Unsubscribe | null = null;

function stopProfileListener() {
  if (profileUnsub) {
    profileUnsub();
    profileUnsub = null;
  }
}

async function ensureUserProfile(uid: string, photoUrl: string | null) {
  const storedName = loadStoredUsername();
  if (!storedName) {
    setSnapshot({ profile: null, loading: false });
    return;
  }

  const now = new Date().toISOString();
  const nextProfile: UserProfile = {
    uid,
    screenName: storedName,
    avatarUrl: photoUrl,
    tosAcceptedAt: now,
    privacyAcceptedAt: now,
  };
  await setDoc(doc(db, "users", uid), {
    ...nextProfile,
    createdAt: now,
  }, { merge: true });
  setSnapshot({ profile: nextProfile, loading: false });
}

function startProfileListener(uid: string) {
  stopProfileListener();
  profileUnsub = onSnapshot(
    doc(db, "users", uid),
    snap => {
      if (snap.exists()) {
        setSnapshot({
          profile: snap.data() as UserProfile,
          loading: false,
        });
        return;
      }
      void ensureUserProfile(uid, auth.currentUser?.photoURL ?? null);
    },
    e => {
      console.error("Firestore profile listen failed:", e);
      setSnapshot({ profile: null, loading: false });
    },
  );
}

async function ensureAnonymousSession() {
  if (auth.currentUser) return;
  if (!anonymousSignInInFlight) {
    anonymousSignInInFlight = (async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        await signInAnonymously(auth);
      } finally {
        anonymousSignInInFlight = null;
      }
    })();
  }
  await anonymousSignInInFlight;
}

onAuthStateChanged(auth, u => {
  stopProfileListener();
  if (u) {
    setSnapshot({ user: u, loading: true });
    startProfileListener(u.uid);
    return;
  }

  setSnapshot({ user: null, profile: null, loading: true });
  void ensureAnonymousSession().catch(e => {
    console.error("Anonymous sign-in failed:", e);
    setSnapshot({ loading: false });
  });
});

export function useAuth() {
  const [state, setState] = useState<AuthSnapshot>(snapshot);
  useEffect(() => {
    // Sync immediately in case the snapshot changed between module load and
    // this component mounting.
    setState(snapshot);
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return state;
}

export async function saveUsernameProfile(screenName: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Not signed in.");
  }
  const normalized = screenName.trim().slice(0, 20);
  if (!normalized) {
    throw new Error("Please enter a username.");
  }

  const now = new Date().toISOString();
  const profile: UserProfile = {
    uid: user.uid,
    screenName: normalized,
    avatarUrl: user.photoURL ?? null,
    tosAcceptedAt: now,
    privacyAcceptedAt: now,
  };
  await setDoc(doc(db, "users", user.uid), {
    ...profile,
    createdAt: now,
    updatedAt: now,
  }, { merge: true });
  setSnapshot({
    user,
    profile,
    loading: false,
  });
}

export async function signOut() {
  await fbSignOut(auth);
}
